import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BACKEND_URL } from '../../utils/config';
import './ProfileShareModal.css';

const Preview = ({ images = [] }) => (
  <span className="profile-share-folder-previews" data-count={Math.min(images.length, 4)}>
    {images.length ? images.slice(0, 4).map((url, i) => <span className="profile-share-cell" key={i}><img src={url} alt="" loading="lazy" /></span>) : <span className="profile-share-cell empty">◇</span>}
  </span>
);

const FolderGrid = ({ title, folders, onOpen, onAdd }) => !folders?.length ? null : (
  <section className="profile-share-group">
    <div className="profile-share-group-heading">{title} <span>{folders.length}</span></div>
    <div className="profile-share-grid">
      {folders.map(folder => (
        <div className="profile-share-folder" key={folder.id}>
          <button className="profile-folder-open" onClick={() => onOpen(folder)} disabled={!folder.shareUrl} aria-label={`Browse ${folder.name}`}>
            <Preview images={folder.previewImages} />
            <span className="profile-share-folder-name"><strong>{folder.name}</strong><span>{folder.itemCount}</span></span>
          </button>
          {folder.shareUrl && <button className="profile-folder-add" onClick={() => onAdd(folder.shareUrl)} aria-label={`Add all of ${folder.name} to my closet`} title="Add entire collection to my closet">+</button>}
        </div>
      ))}
    </div>
  </section>
);

const ProfileShareModal = ({ isOpen, profile, onClose, loadFolder, onAdd }) => {
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [folder, setFolder] = useState(null);
  const [contents, setContents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const dialogRef = useRef(null);
  const loaderRef = useRef(loadFolder);
  loaderRef.current = loadFolder;

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.scrollTop = 0;
      dialogRef.current.focus();
    }
  }, [folder]);
  useEffect(() => { setAvatarFailed(false); setFolder(null); setContents(null); }, [profile?.id, isOpen]);
  useEffect(() => {
    if (!isOpen || !folder) return undefined;
    let cancelled = false;
    setLoading(true); setError(false); setContents(null);
    (async () => {
      try {
        let result;
        if (loaderRef.current) result = await loaderRef.current(folder);
        else {
          const token = new URL(folder.shareUrl, window.location.origin).searchParams.get('share');
          const response = await fetch(`${BACKEND_URL}/share-bundles/by-token/${encodeURIComponent(token)}`);
          if (!response.ok) throw new Error('Unavailable');
          const data = await response.json();
          result = { title: data.title, shareUrl: folder.shareUrl, items: (folder.kind === 'lookbook' ? data.looks : data.clothingItems || []).map(item => ({ ...item, name: item.clothesName || item.name })) };
        }
        if (!cancelled) setContents(result);
      } catch { if (!cancelled) setError(true); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [isOpen, folder]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    const onKey = event => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'Tab') {
        const items = [...(dialogRef.current?.querySelectorAll('button:not(:disabled), a[href]') || [])];
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = overflow; window.removeEventListener('keydown', onKey); previous?.focus(); };
  }, [isOpen, onClose]);

  if (!isOpen || !profile) return null;
  const name = profile.displayName || profile.username;
  const add = url => { if (url) (onAdd || (link => window.location.assign(link)))(url); };
  const { closets = [], lookbooks = [], isPrivate } = profile;

  return createPortal(
    <div className="profile-share-overlay" onClick={onClose}>
      <div className="profile-share-modal" ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={folder ? folder.name : `${name}'s profile`} onClick={e => e.stopPropagation()}>
        <button className="profile-share-close" onClick={onClose} aria-label="Close">×</button>
        {folder ? <>
          <button className="profile-folder-back" onClick={() => setFolder(null)}>← {name}'s collections</button>
          <div className="profile-collection-heading"><h2>{folder.name}</h2><button onClick={() => add(contents?.shareUrl || folder.shareUrl)} className="profile-add-all">+ Add all</button></div>
          <p className="profile-collection-hint">Choose a piece to add it to your closet.</p>
          {loading && <p role="status">Opening collection…</p>}
          {error && <div role="alert">Couldn’t load this collection. <button onClick={() => setFolder({ ...folder })}>Try again</button></div>}
          {!loading && !error && contents?.items?.length === 0 && <p>This collection is empty.</p>}
          <div className="profile-collection-items">{!loading && contents?.items?.map(item => (
            <button className="profile-collection-item" key={item.id} disabled={!item.shareUrl} onClick={() => add(item.shareUrl)} aria-label={`Add ${item.name} to my closet`}>
              <img src={item.imageUrl} alt={item.name} loading="lazy" />
              <span>{item.name}</span><small>{item.shareUrl ? '+ Add to my closet' : 'Available in the full collection'}</small>
            </button>
          ))}</div>
        </> : <>
          <div className="profile-share-header"><div className="profile-share-avatar">
            {profile.avatarUrl && !avatarFailed ? <img src={profile.avatarUrl} alt="" referrerPolicy="no-referrer" onError={() => setAvatarFailed(true)} /> : <span>{name?.charAt(0).toUpperCase() || 'U'}</span>}
          </div><div className="profile-share-name">{name}</div>{profile.bio && !isPrivate && <div className="profile-share-bio">{profile.bio}</div>}</div>
          {isPrivate ? <div className="profile-share-private">This account is private.</div> : <>
            {!closets.length && !lookbooks.length && <div className="profile-share-private">Nothing here yet.</div>}
            <FolderGrid title="Closets" folders={closets} onOpen={f => setFolder({ ...f, kind: 'closet' })} onAdd={add} />
            <FolderGrid title="Lookbooks" folders={lookbooks} onOpen={f => setFolder({ ...f, kind: 'lookbook' })} onAdd={add} />
          </>}
        </>}
      </div>
    </div>, document.body
  );
};
export default ProfileShareModal;
